import type { KeyboardEvent, MouseEvent } from 'react';
import { useToast } from '../../../hooks';
import { cn } from '../../../utils';
import { copyGuideNumber, guideLabel } from '../../../utils/guideNumber';

interface CopyableGuideNumberProps {
    guideNumber: string | null | undefined;
    fallbackId?: number | null;
    className?: string;
    showLabel?: boolean;
}

export function CopyableGuideNumber({
    guideNumber,
    fallbackId,
    className,
    showLabel = true,
}: CopyableGuideNumberProps) {
    const { success, error } = useToast();
    const normalizedGuideNumber = guideNumber?.replace(/\D/g, '') ?? '';
    const canCopy = normalizedGuideNumber.length === 10;
    const label = guideLabel(guideNumber, fallbackId);
    const displayValue = showLabel ? label : label.replace(/^Guía\s+/, '');
    const formattedGuideNumber = label.replace(/^Guía\s+/, '');

    const copy = async (): Promise<void> => {
        try {
            if (await copyGuideNumber(normalizedGuideNumber)) {
                success('Los 10 dígitos se copiaron al portapapeles.');
                return;
            }

            error('La guía todavía no está disponible para copiar.');
        } catch {
            error('No se pudo copiar la guía. Inténtalo de nuevo.');
        }
    };

    const handleCopy = (event: MouseEvent<HTMLSpanElement>): void => {
        event.preventDefault();
        event.stopPropagation();
        void copy();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        void copy();
    };

    if (!canCopy) {
        return <span className={className} translate="no">{displayValue}</span>;
    }

    return (
        <span
            role="button"
            tabIndex={0}
            className={cn(
                'inline-flex min-h-11 max-w-full cursor-copy items-center rounded-lg px-1 text-left transition hover:bg-primary/10 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45',
                className,
            )}
            aria-label={`Copiar ${label}`}
            title="Copiar guía"
            translate="no"
            onClick={handleCopy}
            onKeyDown={handleKeyDown}
        >
            {showLabel ? 'Guía' : null}
            <span className={cn(showLabel && 'ml-1', 'underline decoration-primary/45 underline-offset-4')}>
                {formattedGuideNumber}
            </span>
        </span>
    );
}
